import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Shield, 
  Users,
  Home,
  ChevronLeft,
  ChevronRight,
  Settings,
  Bot,
  Database,
  TrendingUp,
  Menu,
  MessageSquare,
  Star,
  AlertTriangle,
  UserCheck,
  Activity,
  Radio,
  Mail,
  Search
} from 'lucide-react';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarFooter, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';

const menuGroups = [
  {
    label: 'Dashboard',
    items: [
      {
        name: 'Main Dashboard',
        path: '/admin',
        icon: Home
      }
    ]
  },
  {
    label: 'Bot Management',
    items: [
      {
        name: 'Bot Checker',
        path: '/admin/bot-checker',
        icon: Search
      },
      {
        name: 'Bot Connection Errors',
        path: '/admin/bot-errors',
        icon: AlertTriangle
      },
      {
        name: 'Premium Bots',
        path: '/admin/premium-bots',
        icon: Star
      },
      {
        name: 'Prop Trading Bots',
        path: '/admin/prop-trading',
        icon: TrendingUp
      }
    ]
  },
  {
    label: 'Data & Analytics',
    items: [
      {
        name: 'TradingView Crawler',
        path: '/admin/tradingview-crawler',
        icon: Database
      },
      {
        name: 'Market Data Analysis',
        path: '/admin/market-data-analysis',
        icon: BarChart3
      },
      {
        name: 'Trading Pattern Analysis',
        path: '/admin/trading-pattern-analysis',
        icon: TrendingUp
      },
        {
          name: 'Bot Signal Tracking',
          path: '/admin/tracking-bot-signal',
          icon: Radio
        },
        {
          name: 'Bot Merging',
          path: '/admin/merge-bots',
          icon: Activity
        },
        {
          name: 'Conditional Skip',
          path: '/admin/conditional-skip',
          icon: Settings
        },
      {
        name: 'WLLS Analysis',
        path: '/admin/wlls',
        icon: BarChart3
      },
      {
        name: 'Market Alert Settings',
        path: '/admin/market-alert-settings',
        icon: AlertTriangle
      }
    ]
  },
  {
    label: 'AI Systems',
    items: [
      {
        name: 'AI Assistant Management',
        path: '/admin/ai-assistant/management',
        icon: MessageSquare
      },
      {
        name: 'AI Assistant Analytics',
        path: '/admin/ai-assistant/analytics',
        icon: BarChart3
      }
    ]
  },
  {
    label: 'Account Management',
    items: [
      {
        name: 'User Management',
        path: '/admin/users',
        icon: Users
      },
      {
        name: 'Email Notifications',
        path: '/admin/email-notifications',
        icon: Mail
      },
      {
        name: 'CTrader Accounts',
        path: '/admin/ctrader-accounts',
        icon: Database
      },
      {
        name: 'Signal CTrader',
        path: '/admin/ctrader-signals',
        icon: Activity
      }
    ]
  },
  {
    label: 'Affiliate System',
    items: [
      {
        name: 'Affiliate Dashboard',
        path: '/admin/affiliate',
        icon: Users
      },
      {
        name: 'Manage Affiliates',
        path: '/admin/affiliate/affiliates',
        icon: Users
      },
      {
        name: 'Usage Limits',
        path: '/admin/affiliate/limits',
        icon: UserCheck
      },
      {
        name: 'Referral Tracking',
        path: '/admin/affiliate/tracking',
        icon: BarChart3
      },
      {
        name: 'Flagged Activity',
        path: '/admin/affiliate/flagged',
        icon: Shield
      },
      {
        name: 'Data Backfill Tool',
        path: '/admin/affiliate/backfill',
        icon: Database
      }
    ]
  },
  {
    label: 'Settings',
    items: [
      {
        name: 'App Settings',
        path: '/admin/app-settings',
        icon: Settings
      },
      {
        name: 'Telegram Bot',
        path: '/admin/telegram-bot-management',
        icon: MessageSquare
      },
      {
        name: 'Position Mappings',
        path: '/admin/position-mappings',
        icon: Database
      },
      {
        name: 'App Refresh',
        path: '/admin/app-refresh',
        icon: Bot
      }
    ]
  }
];

export const AdminSidebar = () => {
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // Check if user has admin permission
  const isAdmin = user && ['admin', 'superadmin'].includes(user.user_metadata?.role || '');
  
  // If not admin, don't render the sidebar at all
  if (!isAdmin) {
    return null;
  }
  
  return (
    <>
      {isCollapsed && (
        <Button 
          onClick={toggleSidebar} 
          className={cn(
            "fixed left-3 z-20 h-9 w-9 rounded-xl p-0 shadow-lg bg-gradient-to-br from-primary/90 to-primary border-0 hover:from-primary hover:to-primary/80 transition-all duration-300 hover:scale-105",
            isMobile ? "top-10 safe-area-top" : "top-3"
          )}
          size="icon" 
          variant="outline"
        >
          <Menu size={16} className="text-primary-foreground" />
        </Button>
      )}
      
      <Sidebar className="border-r border-border/50 bg-gradient-to-b from-background via-background/95 to-muted/20 backdrop-blur-xl">
        <SidebarHeader className="border-b border-border/30 bg-gradient-to-r from-muted/20 to-background/50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className={cn(
            "mx-0 px-6 py-4 rounded-none my-0 relative transition-all duration-300"
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center transition-all duration-300",
                  isCollapsed ? "scale-75" : "scale-100"
                )}>
                  <Shield size={20} className="text-primary" />
                </div>
                {!isCollapsed && (
                  <div>
                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                      Admin Panel
                    </h2>
                    <p className="text-xs text-muted-foreground">System Management</p>
                  </div>
                )}
              </div>
              <button 
                onClick={toggleSidebar} 
                className={cn(
                  "p-2 rounded-xl hover:bg-primary/10 transition-all duration-300 group hover:scale-105", 
                  !isCollapsed ? "" : "hidden"
                )}
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {isCollapsed ? <ChevronRight size={18} className="relative z-10" /> : <ChevronLeft size={18} className="relative z-10" />}
                </div>
              </button>
            </div>
          </div>
        </SidebarHeader>
        
        <SidebarContent className="px-2 py-4 custom-scrollbar scrollbar-thin scrollbar-thumb-border/40 scrollbar-track-transparent hover:scrollbar-thumb-border/60 overflow-y-auto">
          <div className="space-y-6">
            {menuGroups.map((group, groupIndex) => (
              <SidebarGroup key={groupIndex}>
                <SidebarGroupLabel className="text-sm font-semibold text-muted-foreground/80 mb-3 px-3 uppercase tracking-wider">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-1">
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <SidebarMenuItem key={item.path} className="group/item">
                          <SidebarMenuButton 
                            asChild 
                            isActive={isActive} 
                            tooltip={isCollapsed ? item.name : undefined}
                            className={cn(
                              "relative rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]",
                              isActive 
                                ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent border border-primary/20 shadow-md shadow-primary/10" 
                                : "hover:bg-gradient-to-r hover:from-muted/50 hover:to-transparent hover:border-border/30"
                            )}
                          >
                            <Link to={item.path} className="flex items-center gap-3 w-full">
                              <div className={cn(
                                "p-2 rounded-lg transition-all duration-300 group-hover/item:scale-110",
                                isActive 
                                  ? "bg-primary/20 text-primary shadow-sm" 
                                  : "text-muted-foreground group-hover/item:bg-muted/30 group-hover/item:text-foreground"
                              )}>
                                <item.icon size={18} />
                              </div>
                              <span className={cn(
                                "font-medium transition-all duration-300",
                                isActive ? "text-foreground" : "text-muted-foreground group-hover/item:text-foreground"
                              )}>
                                {item.name}
                              </span>
                              {isActive && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary to-primary/50 rounded-r-full shadow-lg shadow-primary/30" />
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </div>
        </SidebarContent>

        <SidebarFooter className="border-t border-border/30 bg-gradient-to-r from-muted/10 to-background/50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className={cn("p-4", isCollapsed ? "flex justify-center" : "space-y-4")}>
            <Link to="/" className="flex items-center gap-2 text-sm text-primary hover:underline transition-colors duration-300">
              <Home size={16} />
              {!isCollapsed && <span>Back to Main App</span>}
            </Link>
          </div>
        </SidebarFooter>
      </Sidebar>
    </>
  );
};
